/* eslint-disable max-len */
import type { EmailMailboxId } from '@dataTypes/email.type'
import {
  CoversheetCustomFieldType,
  CoversheetFieldSource,
} from '@dataTypes/fax.type'
import { FaxTabData, KeyValuePair, TabData } from '@dataTypes/message.types'

export const MESSAGE_TABS = [
  { label: 'Contact', value: 'contact' },
  { label: 'Chat', value: 'chat' },
  { label: 'Call logs', value: 'calls' },
  { label: 'Fax', value: 'fax' },
  { label: 'Email', value: 'email' },
]

export const EMAIL_PRIMARY_MAILBOXES: {
  key: EmailMailboxId
  text: string
  icon: string
}[] = [
  { key: 'inbox', text: 'Inbox', icon: 'mgc_inbox_fill' },
  { key: 'drafts', text: 'Drafts', icon: 'mgc_inbox_2_fill' },
  { key: 'sent', text: 'Sent', icon: 'mgc_send_plane_fill' },
  { key: 'starred', text: 'Starred', icon: 'mgc_star_fill' },
  { key: 'all', text: 'All mail', icon: 'mgc_mail_fill' },
  { key: 'archive', text: 'Archive', icon: 'mgc_archive_fill' },
  { key: 'spam', text: 'Spam', icon: 'mgc_alert_fill' },
  { key: 'trash', text: 'Trash', icon: 'mgc_delete_2_fill' },
]

export const EMAIL_CUSTOM_FOLDERS = [
  { id: 'f1', name: 'Dr. Jones' },
  { id: 'f2', name: 'Insurance claims' },
  { id: 'f3', name: 'Legal docs' },
]

export const CHAT_LEFT_BUTTONS: TabData[] = [
  {
    key: 'ai',
    text: 'AI Feature',
    icon: 'mgc_magic_3_fill',
    type: 'mgc',
  },
  { key: 'emoji', text: 'Emojis', icon: 'EmojiPicker', type: 'builtin' },
  {
    key: 'variable',
    text: 'Variable serves as a dynamic placeholder to help you save time.',
    icon: 'ti ti-variable',
    type: 'ti',
  },
  { key: 'url', text: 'URL', icon: 'CreateLink', type: 'builtin' },
]

export const AI_BUTTONS: TabData[] = [
  { key: 'reply', text: 'Reply to this message', icon: '' },
  { key: 'summarize', text: 'Summarize', icon: '' },
]

export const CALL_TYPES_ICON = {
  Outgoing: 'mgc_arrow_right_up_line',
  Incoming: 'mgc_arrow_left_down_line',
  Missed: 'mgc_arrow_to_down_line',
  Decline: 'mgc_corner_down_right_line',
}

export const CALLS_GRID_PAGE_SIZE = 10

export const CHAT_RIGHT_BUTTONS: TabData[] = [
  {
    key: 'mic',
    text: 'Microphone',
    icon: 'mgc_mic_fill',
  },
  { key: 'send', text: 'Send', icon: 'mgc_send_fill' },
]

export const CHAT_HEADING_BUTTONS: TabData[] = [
  { key: 'call', text: 'Call', icon: 'mgc_phone_fill' },
]

export const CHANNEL_SELECTION: TabData[] = [
  { key: 'All', text: 'All', icon: '' },
  { key: 'SMS', text: 'SMS', icon: '' },
  { key: 'Messenger', text: 'Messenger', icon: '' },
  { key: 'Instagram', text: 'Instagram', icon: '' },
]

export const PROMPTS: KeyValuePair = {
  rewrite: 'Rephrase the message delimited by back-ticks:',
  formalize:
    'Set the tone into a more formal scenario of the following message:',
  summarize:
    'Summarize the following message. Make sure that the output is shorter than the given text:',
  'list items': 'Format the text below, make the format in list form:',
  simplify:
    "Simplify the following message. Make sure to use layman's terms that are easy for everyone to understand.",
}

export const FAX_SIDE_PANEL_SELECTION: FaxTabData[][] = [
  [
    { key: 'inbox', text: 'Inbox', icon: 'mgc_inbox_fill' },
    { key: 'draft', text: 'Drafts', icon: 'mgc_inbox_2_fill' },
    { key: 'sent', text: 'Sent', icon: 'mgc_send_plane_fill' },
    { key: 'history', text: 'History', icon: 'mgc_history_anticlockwise_fill' },
  ],
  [
    { key: 'archive', text: 'Archive', icon: 'mgc_archive_fill' },
    { key: 'trash', text: 'Trash', icon: 'mgc_delete_2_fill' },
    { key: 'blocked', text: 'Blocked', icon: 'mgc_forbid_circle_fill' },
  ],
]

export const FAX_FOLDER_MORE_ITEMS = [
  { id: 'rename', text: 'Rename', iconCss: 'mgc_pencil_fill' },
  { id: 'delete', text: 'Delete', iconCss: 'mgc_delete_2_fill' },
] as const

export const FAX_LABEL_MORE_ITEMS = [
  { id: 'edit', text: 'Edit', iconCss: 'mgc_pencil_fill' },
  { id: 'delete', text: 'Delete', iconCss: 'mgc_delete_2_fill' },
] as const

export const FAX_MESSAGE_MAX_CHAR_COUNT = 1000

export const FAX_BLOCK_REASON_PRESET_VALUES = [
  'Spam fax',
  'Unsolicited marketing fax',
  'Wrong number',
  'Harassment or abuse',
  'Suspected fraud',
  'Do not contact request',
  'Other',
] as const

export const FAX_ERRORS = {
  number: {
    numberLength: 'Fax number must be 10–15 digits (e.g. +1 949 775 2183)',
    numbersOnly:
      'Fax number can only include digits, +, spaces, dashes, or parentheses.',
  },
  message: 'Fax message is required',
}

export const preferredCallMethodOptions = [
  { text: 'Cell phone', id: 'cellphone' },
  { text: 'Home number', id: 'home_phone' },
  { text: 'Work number', id: 'work_phone' },
]

export const AI_CHAR_LIMIT = 200

export const COVERSHEET_FIELD_OPTIONS: {
  id: CoversheetCustomFieldType
  label: string
}[] = [
  { id: 'input', label: 'Input field' },
  { id: 'date', label: 'Date field' },
  { id: 'checklist', label: 'Checklist' },
]

export const COVERSHEET_FIELD_SOURCE_OPTIONS: Record<
  CoversheetCustomFieldType,
  { id: CoversheetFieldSource; label: string }[]
> = {
  input: [
    { id: 'custom', label: 'Custom input field' },
    { id: 'recipient_name', label: 'Recipient name' },
    { id: 'recipient_fax', label: 'Recipient fax number' },
    { id: 'recipient_email', label: 'Recipient email' },
    { id: 'sender_name', label: 'Sender name' },
    { id: 'sender_fax', label: 'Sender fax number' },
    { id: 'sender_email', label: 'Sender email' },
    { id: 'current_date', label: 'Current date' },
  ],
  date: [
    { id: 'custom', label: 'Custom date field' },
    { id: 'current_date', label: 'Current date' },
  ],
  checklist: [],
}
